import React, { useState, useEffect, useCallback } from 'react';
import { X, History, Plus, Pencil, Trash2, Filter } from 'lucide-react';
import { condoApi } from '../../../services/api';
import { logger } from '../../../services/logger';
import { cn } from '../../../utils/cn';

interface LembreteHistoryEntry {
  id: string;
  lembrete_id: string;
  action: 'created' | 'updated' | 'deleted';
  content_html: string;
  actor_name: string;
  created_at: string;
}

interface LembreteHistoryModalProps {
  open: boolean;
  onClose: () => void;
  customerId: string | undefined;
  lightTheme?: boolean;
}

type ActionFilter = 'all' | 'created' | 'updated' | 'deleted';

const ACTION_LABELS: Record<LembreteHistoryEntry['action'], string> = {
  created: 'Criado',
  updated: 'Editado',
  deleted: 'Excluído',
};

/**
 * Append-only history of create/update/delete actions on lembretes — including
 * ones that no longer exist, so operators can see who deleted a reminder and when.
 */
export const LembreteHistoryModal: React.FC<LembreteHistoryModalProps> = ({
  open,
  onClose,
  customerId,
  lightTheme = false,
}) => {
  const [entries, setEntries] = useState<LembreteHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionFilter, setActionFilter] = useState<ActionFilter>('all');

  const fetchHistory = useCallback(async () => {
    if (!customerId || !open) return;
    setLoading(true);
    setError(null);
    try {
      const response = await condoApi.getLembreteHistory(customerId);
      setEntries(response.data?.data || []);
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to fetch history';
      logger.error('LembreteHistoryModal', 'Error fetching history', err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [customerId, open]);

  useEffect(() => {
    if (open) fetchHistory();
  }, [fetchHistory, open]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  if (!open) return null;

  const filteredEntries = actionFilter === 'all' ? entries : entries.filter(e => e.action === actionFilter);

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      time: date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
    };
  };

  const actionBadge = (action: LembreteHistoryEntry['action']) => {
    const styles: Record<string, string> = {
      created: lightTheme ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-green-500/20 text-green-400',
      updated: lightTheme ? 'bg-blue-100 text-blue-700 border border-blue-200' : 'bg-blue-500/20 text-blue-400',
      deleted: lightTheme ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-red-500/20 text-red-400',
    };
    const Icon = action === 'created' ? Plus : action === 'updated' ? Pencil : Trash2;
    return (
      <div className={cn('flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium', styles[action])}>
        <Icon className="h-3 w-3" />
        {ACTION_LABELS[action]}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className={cn(
        'relative w-full max-w-2xl max-h-[80vh] rounded-xl shadow-2xl border flex flex-col',
        lightTheme ? 'bg-white border-gray-200' : 'bg-gray-900 border-gray-700'
      )}>
        {/* Header */}
        <div className={cn('flex items-center justify-between p-4 border-b', lightTheme ? 'border-gray-200' : 'border-gray-700')}>
          <div className="flex items-center gap-3">
            <div className={cn('p-2 rounded-lg', lightTheme ? 'bg-gray-100' : 'bg-gray-700')}>
              <History className={cn('h-5 w-5', lightTheme ? 'text-gray-600' : 'text-gray-300')} />
            </div>
            <h2 className={cn('text-lg font-semibold', lightTheme ? 'text-gray-900' : 'text-white')}>Histórico de Lembretes</h2>
          </div>
          <button
            onClick={onClose}
            className={cn('p-2 rounded-lg transition-colors', lightTheme ? 'text-gray-400 hover:text-gray-700 hover:bg-gray-100' : 'text-gray-400 hover:text-white hover:bg-gray-700')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Filters */}
        <div className={cn('flex flex-wrap items-center gap-3 p-4 border-b', lightTheme ? 'border-gray-200' : 'border-gray-700')}>
          <Filter className={cn('h-4 w-4', lightTheme ? 'text-gray-400' : 'text-gray-500')} />
          <div className={cn('flex gap-1 rounded-lg p-1', lightTheme ? 'bg-gray-100' : 'bg-gray-800')}>
            {(['all', 'created', 'updated', 'deleted'] as ActionFilter[]).map((action) => (
              <button
                key={action}
                onClick={() => setActionFilter(action)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                  actionFilter === action
                    ? lightTheme ? 'bg-white text-gray-800 shadow-sm border border-gray-200' : 'bg-gray-600 text-white'
                    : lightTheme ? 'text-gray-500 hover:text-gray-700 hover:bg-white/60' : 'text-gray-400 hover:text-white hover:bg-gray-700'
                )}
              >
                {action === 'all' ? 'Todos' : ACTION_LABELS[action]}
              </button>
            ))}
          </div>
          <span className={cn('ml-auto text-xs', lightTheme ? 'text-gray-400' : 'text-gray-500')}>
            {filteredEntries.length} {filteredEntries.length === 1 ? 'registro' : 'registros'}
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className={cn('h-8 w-8 border-2 rounded-full animate-spin', lightTheme ? 'border-gray-200 border-t-gray-600' : 'border-gray-600 border-t-white')} />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-500">{error}</p>
              <button
                onClick={fetchHistory}
                className={cn('mt-4 px-4 py-2 text-sm rounded-lg', lightTheme ? 'text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200' : 'text-gray-300 hover:text-white bg-gray-700')}
              >
                Tentar novamente
              </button>
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="text-center py-12">
              <History className={cn('h-12 w-12 mx-auto mb-3', lightTheme ? 'text-gray-300' : 'text-gray-600')} />
              <p className={lightTheme ? 'text-gray-400' : 'text-gray-400'}>Nenhum registro encontrado</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredEntries.map((entry) => {
                const when = formatDateTime(entry.created_at);
                return (
                  <div
                    key={entry.id}
                    className={cn('p-4 rounded-lg border',
                      lightTheme ? 'bg-gray-50 border-gray-200' : 'bg-gray-800 border-gray-700'
                    )}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      {actionBadge(entry.action)}
                    </div>

                    <div
                      dangerouslySetInnerHTML={{ __html: entry.content_html || '<span class="opacity-50">Vazio</span>' }}
                      className={cn(
                        'text-sm whitespace-pre-line leading-snug',
                        entry.action === 'deleted' && 'line-through opacity-70',
                        lightTheme ? 'text-gray-700' : 'text-gray-300'
                      )}
                    />

                    <div className={cn('mt-3 pt-3 border-t text-xs', lightTheme ? 'border-gray-200 text-gray-400' : 'border-gray-700/50 text-gray-500')}>
                      <span className={cn('font-medium', lightTheme ? 'text-gray-700' : 'text-gray-300')}>{entry.actor_name}</span>
                      {' em '}{when.date} às {when.time}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LembreteHistoryModal;
